import { useCallback, useEffect, useState } from "react";
import { captureException } from "@sentry/nextjs";
import { useDataLayer } from "./useDataLayer";
import {
  dreamSearchItemToEcommerceItem,
  ECOMMERCE_EVENT,
  EventName,
  ResultItemForEcommerce,
} from "@portal/app/_common/lib/analytics";
import { DataLayerEvent } from "@portal/app/_common/lib/analytics";
import {
  buildLeadConversionParams,
  LEAD_EVENT,
  LeadConversion,
} from "@portal/app/_common/lib/leadConversion";

type LeadDataLayerEvent = {
  event: typeof LEAD_EVENT;
  hosted_lead: ReturnType<typeof buildLeadConversionParams>;
  completed_lead: null;
  _clear: true;
};

export const useEcommerceEvents = () => {
  const { ready, dataLayer } = useDataLayer();
  const [, setEvents] = useState<Array<DataLayerEvent | LeadDataLayerEvent>>([]);

  const safeTrack = useCallback(
    (buildEvent: () => DataLayerEvent | LeadDataLayerEvent) => {
      try {
        const eventData = buildEvent();
        /**
         * If dataLayer isn't loaded yet for some reason, add it to be processed
         * later in the useEffect below
         */
        if (ready) {
          dataLayer.push(eventData);
        } else {
          setEvents((prevEvents) => [...prevEvents, eventData]);
        }
      } catch (e) {
        captureException(e);
      }
    },
    [dataLayer, ready]
  );

  const trackEcommerce = useCallback(
    (event: EventName, item: ResultItemForEcommerce) =>
      safeTrack(() => ({
        event,
        ecommerce: {
          items: [dreamSearchItemToEcommerceItem(item)],
        },
      })),
    [safeTrack]
  );

  useEffect(() => {
    if (ready) {
      setEvents((prevEvents) => {
        dataLayer.push(...prevEvents);
        return [];
      });
    }
  }, [dataLayer, ready]);

  return {
    trackAddToWishlist: useCallback(
      (item: ResultItemForEcommerce) => trackEcommerce(ECOMMERCE_EVENT.ADD_TO_WISHLIST, item),
      [trackEcommerce]
    ),
    trackViewItem: useCallback(
      (item: ResultItemForEcommerce) => trackEcommerce(ECOMMERCE_EVENT.VIEW_ITEM, item),
      [trackEcommerce]
    ),
    trackAddToCart: useCallback(
      (item: ResultItemForEcommerce) => trackEcommerce(ECOMMERCE_EVENT.ADD_TO_CART, item),
      [trackEcommerce]
    ),
    trackBeginCheckout: useCallback(
      (item: ResultItemForEcommerce) => trackEcommerce(ECOMMERCE_EVENT.BEGIN_CHECKOUT, item),
      [trackEcommerce]
    ),
    trackPurchase: useCallback(
      (item: ResultItemForEcommerce) => trackEcommerce(ECOMMERCE_EVENT.PURCHASE, item),
      [trackEcommerce]
    ),
    trackGenerateLead: useCallback(
      (lead: LeadConversion) =>
        safeTrack(() => ({
          event: LEAD_EVENT,
          hosted_lead: buildLeadConversionParams(lead),
          completed_lead: null,
          // Replace the object so omitted optional context cannot inherit an earlier lead's data.
          _clear: true,
        })),
      [safeTrack]
    ),
  };
};
